import React, { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Icon } from 'semantic-ui-react';
import {
  AttachmentFormGroup,
  AttachmentWrapper,
  StyledDeleteButton
} from '~/pages/Dairy/Newsletter/CreateEditNewsletter/LangTab/Attachments/style';
import { FormFields } from '~/components/FormFields';
import FileUpload from '~/components/FileUpload';
import { useTranslation } from 'react-i18next';
import FaqService from '~/api/services/faq';
import { b64toBlob, handleErrors } from '~/utils/helpers';
import Dialog from '~/components/Dialog';
import { StyledFileLabel, StyledFileWrapper } from './style';
import { applicationPdfType, imageTypes } from '~/Constants';

interface AttachmentsProps {
  index: number
  languageIndex?: number
  control: any
  getValues?: (name: string) => any
}

const Attachments: React.FC<AttachmentsProps> = (props): JSX.Element => {
  const {
    index,
    languageIndex,
    control,
    getValues
  } = props

  const { t } = useTranslation();
  const [fileSource, setFileSource] = useState(null);
  const [deleteFileData, setDeleteFileData] = useState(null)

  const attachments = useFieldArray({
    control,
    name: `questions[${index}].languages[${languageIndex}].files`,
    keyName: 'key'
  });

  function handleDownloadFile(url, originalFilename) {
    const a = document.createElement('a');
    a.href = url;
    a.download = originalFilename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async function handleDownloadPdf(fileName, fileSrc, attachment?) {
    try {
      if (!fileSrc) {
        const response = await FaqService.getFile({ fileName: fileName });
        const type = response.data.split(';')[0].split('data:')[1];
        const blob = b64toBlob(response.data, type);
        const blobUrl = URL.createObjectURL(blob);
        if (imageTypes.find(imageType => `image/${imageType}` === type) || type === applicationPdfType) {
          setFileSource(blobUrl);
        } else {
          handleDownloadFile(blobUrl, attachment?.originalFilename)
        }
      } else {
        const type = attachment.attachment.split(';')[0].split('data:')[1];
        const blob = b64toBlob(attachment.attachment, type);
        const blobUrl = URL.createObjectURL(blob);
        if (imageTypes.find(imageType => `image/${imageType}` === type) || type === applicationPdfType) {
          setFileSource(blobUrl);
        } else {
          handleDownloadFile(blobUrl, attachment.originalFilename)
        }
      }
    } catch (error) {
      handleErrors(error)
    }
  }

  function addAttachmentFile(value, name) {
    const attachmentFile = {
      attachment: value,
      originalFilename: name
    };

    attachments.append(attachmentFile);
  }

  function getFileName(languageIndex, attachmentIndex) {
    return getValues(
      `questions[${index}].languages[${languageIndex}].files[${attachmentIndex}].originalFilename`
    );
  }

  function renderModalPreviewFile() {
    return (
      <Dialog
        open={Boolean(fileSource)}
        handleClose={() => setFileSource(null)}
        textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
        textHeader={t('Preview')}
        textClose={t('Close')}
        type={'preview'}
      />
    );
  }

  async function handleDeleteFile() {
    try {
      await FaqService.deleteFile({ fileName: deleteFileData?.filename })
      const index = attachments.fields.findIndex(file => file?.key === deleteFileData?.key)
      if (index >= 0) {
        attachments.remove(index)
      }
      setDeleteFileData(null)
    } catch (error) {
      handleErrors(error)
    }
  }

  function renderRemoveFileModal() {
    return <Dialog
      isDialogOpen={Boolean(deleteFileData)}
      textHeader={(t('Delete file'))}
      textSubmit={t('Delete')}
      textContent={t('Do you really want to delete {{fileName}}?', { fileName: deleteFileData?.originalFilename })}
      textClose={t('Close')}
      handleClose={() => setDeleteFileData(null)}
      handleSubmit={() => handleDeleteFile()}
      type='abort'
    />
  }

  const files = getValues(`questions[${index}].languages[${languageIndex}].files`)

  return (
    <>

      {renderRemoveFileModal()}
      {files?.length === 0 && <FileUpload
        last={true}
        sizeLimitInMB={10}
        mimeTypes={[
          'pdf', 'txt', 'doc', 'docx', 'csv',
          'xls', 'xlsx', 'bmp', 'jpg', 'jpeg', 'png'
        ]}
        encodeFiles
        list={true}
        onChange={(value, name) => {
          addAttachmentFile(value, name)
        }} />}
      {attachments.fields.map((attachment, attachmentIndex) => {
        return <AttachmentWrapper key={attachment.key}>
          <>
            <AttachmentFormGroup>
              <FormFields.External
                name={`questions[${index}].languages[${languageIndex}].files[${attachmentIndex}].attachment`}
                control={control}
                keyName={'filename'}
                onChange={(value, name) => {
                  addAttachmentFile(value, name)
                }}
                style={{ margin: 0 }}
              >
                <FileUpload
                  encodeFiles
                  sizeLimitInMB={10}
                  accept={{
                    'application/pdf': ['.pdf'],
                    'text/plain': ['.txt'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                    'text/csv': ['.csv'],
                    'application/vnd.ms-excel': ['.xls'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                    'image/bmp': ['.bmp'],
                    'image/jpeg': ['.jpg', '.jpeg'],
                    'image/png': ['.png']
                  }}
                  list={true}
                  last={attachmentIndex === files.length - 1}
                  limit={attachmentIndex >= 2}
                >
                  {Boolean(getFileName(languageIndex, attachmentIndex)) && (
                    <StyledFileWrapper>
                      <StyledFileLabel
                        basic
                        color='blue'
                        onClick={() => {
                          handleDownloadPdf(
                            getValues(
                              `questions[${index}].languages[${languageIndex}].files[${attachmentIndex}].filename`
                            ),
                            getValues(`questions[${index}].languages[${languageIndex}].files[${attachmentIndex}].attachment`),
                            attachment
                          );
                        }}
                      >
                        <Icon name='file' />
                        <div className='fileName'>{getFileName(languageIndex, attachmentIndex)}</div>
                      </StyledFileLabel>

                      <StyledDeleteButton
                        onClick={() => {
                          if (attachment['attachment']) {
                            attachments.remove(attachmentIndex)
                            return
                          }
                          setDeleteFileData(attachment)
                        }}
                        size={'small'}
                        basic
                        color='red'
                        type={'button'}
                        icon='trash' />
                    </StyledFileWrapper>
                  )}
                </FileUpload>
              </FormFields.External>
            </AttachmentFormGroup>
          </>
        </AttachmentWrapper>
      })}
      {fileSource && renderModalPreviewFile()}
    </>
  );
};

export default Attachments;
